import { invariant } from "@searchui-searchforms/shared/utils";
import type { FieldComposerConfig } from "@/features/form-fields";
import type { Field } from "@/entities/form-metadata";
import { EventAssertion, FieldType } from "@/entities/form-metadata";
import { getHtmlString } from "@/shared/utils/htmlMarkupGeneration";
import { FieldComposerExactnessType, FieldComposerFieldType } from "../types";

const getExactnessType = (assertion?: string): FieldComposerConfig["exactnessType"] => {
    switch (assertion) {
        case EventAssertion.Year:
            return FieldComposerExactnessType.Year;
        case EventAssertion.Place:
            return FieldComposerExactnessType.Place;
        default:
            return FieldComposerExactnessType.Generic;
    }
};

const mapFieldType = (fieldType: string): FieldComposerConfig["fieldType"] => {
    switch (fieldType) {
        case FieldType.Integer:
            return FieldComposerFieldType.Integer;
        case FieldType.Year:
        case FieldType.YearRange:
            return FieldComposerFieldType.Year;
        case FieldType.Place:
            return FieldComposerFieldType.Place;
        case FieldType.LimitedValues:
            return FieldComposerFieldType.LimitedValues;
        case FieldType.ValueRange:
            return FieldComposerFieldType.ValueRange;
        default:
            return FieldComposerFieldType.String;
    }
};

export const fieldToConfig = ({ features, label, meta, fieldType, assertion }: Field<string>): FieldComposerConfig => {
    const baseConfig = {
        required: features?.required,
        label,
        placeholder: meta?.placeHolder?.text,
        showExactness: features?.showExactness,
        exactnessType: getExactnessType(assertion),
        guidance: meta?.fieldUsageText && {
            exampleText: meta.fieldUsageText.exampleText,
            descriptionHtml: getHtmlString({
                message: meta.fieldUsageText.usageHelp,
                formatTags: meta.fieldUsageText.usageHelpFormatTags,
            }),
            viewType: meta.fieldUsageText.viewType,
            viewTitle: meta.fieldUsageText.viewTitle,
            fieldName: assertion,
        },
    } as const satisfies Partial<FieldComposerConfig>;

    const mappedFieldType = mapFieldType(fieldType);

    switch (mappedFieldType) {
        case FieldComposerFieldType.LimitedValues:
            invariant(features.options, "features.options is required");

            return {
                ...baseConfig,
                fieldType: FieldComposerFieldType.LimitedValues,
                options: features.options,
            };
        case FieldComposerFieldType.ValueRange:
            invariant(features.range, "features.range is required");

            return {
                ...baseConfig,
                fieldType: FieldComposerFieldType.ValueRange,
                range: features.range,
            };
        default:
            return {
                ...baseConfig,
                fieldType: mappedFieldType,
            };
    }
};
